// filepath: apps/web/src/features/cabinet/chrome/useLocalePreference.ts
//
// FR-UI-CABINET-2 AC-12 / AC-13 — React hook wrapping the
// localStorage-backed locale preference store.
//
// Drop-in for any consumer that needs to read or write the locale.
// The `useLocalePreference` hook is the source of truth for
// components; the underlying `i18nLocalePreference` module is
// pure (no React imports) and is also used at boot by `main.tsx`
// to resolve the initial locale.

import { useCallback, useSyncExternalStore } from "react";
import i18n from "@/i18n/i18n";
import { setDocumentLocale, type Locale } from "@/runtime/setDocumentLocale";
import {
  subscribeLocalePreference,
  getLocaleSnapshot,
  getServerLocaleSnapshot,
  setLocalePreference,
  type SupportedLocale,
} from "./i18nLocalePreference";

/**
 * Returns the currently-stored locale preference (null if the user
 * has never chosen one) and a setter that persists + applies the
 * change. The setter also calls `i18n.changeLanguage` and
 * `setDocumentLocale` so every locale-aware surface updates in a
 * single tick.
 */
export function useLocalePreference(): {
  locale: SupportedLocale | null;
  setLocale: (next: SupportedLocale) => void;
} {
  const locale = useSyncExternalStore(
    subscribeLocalePreference,
    getLocaleSnapshot,
    getServerLocaleSnapshot,
  );
  const setLocale = useCallback((next: SupportedLocale) => {
    setLocalePreference(next);
    // i18next changeLanguage fires the `languageChanged` event which
    // setDocumentLocale already subscribes to (see
    // apps/web/src/runtime/setDocumentLocale.ts). The explicit call
    // here is a defensive no-op for the case where setDocumentLocale
    // hasn't been installed yet (e.g. unit tests).
    void i18n.changeLanguage(next).then(() => {
      setDocumentLocale(next as Locale);
    });
  }, []);
  return { locale, setLocale };
}
